import type React from "react";
import { useState } from "react";
import { reportClientError } from "../../../frontend/lib/error-reporter.ts";
import { apiRequest } from "../../../shared/lib/api-client.ts";
import { PROVIDER_NAMES } from "../../../shared/lib/scoring-utils.ts";
import {
  type ProjectSubmitQueue,
  parseCommunityStatus,
  type ScoringProgress,
  type Toast,
} from "../../../shared/types/scoring.ts";
import { submitProject as submitProjectRequest } from "../../leaderboard/api/leaderboard-api.ts";
import type { Project } from "../../leaderboard/types/index.ts";

interface RescoreModalProps {
  project: Project;
  onClose: () => void;
  onRescoreSubmitted: (projectId: string, progress: ScoringProgress) => void;
  addToast: (type: Toast["type"], title: string, message: string) => void;
}

export function RescoreModal({
  project,
  onClose,
  onRescoreSubmitted,
  addToast,
}: RescoreModalProps) {
  const [rescoreData, setRescoreData] = useState({
    founderName: "",
    founderTwitter: "",
    founderRole: "",
    raiseAmount: "",
    raiseInvestors: "",
    raiseLink: "",
  });
  const [twitterError, setTwitterError] = useState("");

  function handleOverlayKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
    }
  }

  async function submitRescore(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (rescoreData.founderName && rescoreData.founderTwitter) {
        const founderRes = await apiRequest("/v1/admin/founder-override", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectId: project.id,
            founders: [
              {
                name: rescoreData.founderName,
                role: rescoreData.founderRole || undefined,
                twitter: rescoreData.founderTwitter.replace(/^@/, ""),
              },
            ],
          }),
        });

        if (!founderRes.ok) {
          const msg =
            founderRes.status === 403 ? "Admin access required" : "Could not update founder info";
          addToast("error", "Update failed", msg);
          return;
        }

        addToast("success", "Founder updated", "Pedigree score recalculated");
      }

      const payload = await submitProjectRequest({
        twitterHandle: project.twitter_handle,
        forceRescore: true,
      });

      const auditRaw = payload.audit as ProjectSubmitQueue | undefined;
      const communityRaw = payload.community as ProjectSubmitQueue | undefined;
      const now = Date.now();
      const usingCachedHourlyRun =
        auditRaw?.status === "already_queued" || auditRaw?.isNew === false;
      const communityStatus =
        communityRaw?.status === "skipped"
          ? parseCommunityStatus(undefined, "skipped")
          : parseCommunityStatus(
              {
                status: "queued",
                queueState: null,
                queuePosition: null,
                waitingMs: null,
                jobId: communityRaw?.jobId ?? null,
                progress: null,
                latestReport: null,
              },
              "queued"
            );

      const progress: ScoringProgress = {
        projectId: project.id,
        handle: project.twitter_handle,
        status: "queued",
        startedAt: now,
        updatedAt: now,
        lastPolledAt: 0,
        jobId: auditRaw?.jobId ?? null,
        queueState: "waiting",
        queuePosition: null,
        waitingMs: 0,
        usingCachedHourlyRun,
        completedProviders: [],
        failedProviders: [],
        totalProviders: Object.keys(PROVIDER_NAMES).length,
        community: communityStatus,
      };

      onRescoreSubmitted(project.id, progress);
      onClose();

      if (usingCachedHourlyRun) {
        addToast(
          "info",
          "Using Cached Hourly Run",
          `@${project.twitter_handle} already has an hourly job. Tracking the existing run.`
        );
      } else {
        addToast("info", "Rescore queued", `@${project.twitter_handle} will be rescored`);
      }
    } catch (err) {
      console.error("Failed to rescore:", err);
      reportClientError({ scope: "RescoreModal", error: err });
      addToast(
        "error",
        "Rescore failed",
        err instanceof Error ? err.message : "Could not queue rescore"
      );
    }
  }

  return (
    <div
      className="rescore-modal-overlay"
      onClick={onClose}
      onKeyDown={handleOverlayKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rescore-modal-title"
    >
      <div
        className="rescore-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="rescore-modal-header">
          <h3 id="rescore-modal-title">Rescore with Additional Info</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        <form onSubmit={submitRescore} className="rescore-form">
          <div className="form-section">
            <div className="form-section-title">Founder Information</div>
            <p className="form-section-desc">Add founder details to improve pedigree scoring</p>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rescore-founder-name">Founder Name</label>
                <input
                  id="rescore-founder-name"
                  type="text"
                  placeholder="e.g., Hayden Adams"
                  value={rescoreData.founderName}
                  onChange={(e) => setRescoreData({ ...rescoreData, founderName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="rescore-founder-twitter">Founder Twitter</label>
                <input
                  id="rescore-founder-twitter"
                  type="text"
                  placeholder="e.g., @haaborshchev"
                  value={rescoreData.founderTwitter}
                  onChange={(e) => {
                    setRescoreData({ ...rescoreData, founderTwitter: e.target.value });
                    if (twitterError) setTwitterError("");
                  }}
                  onBlur={() => {
                    const val = rescoreData.founderTwitter.trim();
                    if (val && !/^@?[A-Za-z0-9_]{1,15}$/.test(val)) {
                      setTwitterError("Invalid Twitter handle");
                    }
                  }}
                />
                {twitterError && <div className="form-helper-error">{twitterError}</div>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rescore-founder-role">Role (optional)</label>
              <input
                id="rescore-founder-role"
                type="text"
                placeholder="e.g., CEO, Founder, CTO"
                value={rescoreData.founderRole}
                onChange={(e) => setRescoreData({ ...rescoreData, founderRole: e.target.value })}
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">Raise Information</div>
            <p className="form-section-desc">Add funding details if not automatically detected</p>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rescore-raise-amount">Raise Amount</label>
                <input
                  id="rescore-raise-amount"
                  type="text"
                  placeholder="e.g., $2M, $10M"
                  value={rescoreData.raiseAmount}
                  onChange={(e) => setRescoreData({ ...rescoreData, raiseAmount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="rescore-raise-investors">Investors</label>
                <input
                  id="rescore-raise-investors"
                  type="text"
                  placeholder="e.g., a16z, Paradigm"
                  value={rescoreData.raiseInvestors}
                  onChange={(e) =>
                    setRescoreData({ ...rescoreData, raiseInvestors: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rescore-raise-link">Source Link (optional)</label>
              <input
                id="rescore-raise-link"
                type="text"
                placeholder="e.g., https://techcrunch.com/..."
                value={rescoreData.raiseLink}
                onChange={(e) => setRescoreData({ ...rescoreData, raiseLink: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn">
              Rescore Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
